import React from 'react';
import {
    Box,
    IconButton,
    Typography,
    alpha,
    darken,
    makeStyles,
    useTheme,
} from '@material-ui/core';
import { Mic, Pause } from '@material-ui/icons';
import { Trash2 } from 'react-feather';
import { formatRecorderDuration } from 'src/views/OmniChannel/utils/audioRecorder';
import type { TAudioRecorderStatus } from 'src/views/OmniChannel/utils/audioRecorder';
import { AudioWaveform } from './AudioWaveform';
import { InlineAudioPlayer } from './InlineAudioPlayer';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0,
        padding: theme.spacing(0.75, 1.25),
        borderRadius: 999,
        backgroundColor: darken(theme.palette.background.paper, 0.05),
        color: theme.palette.text.primary,
    },
    iconButton: {
        color: 'inherit',
        padding: 6,
        flexShrink: 0,
    },
    center: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0,
    },
    timer: {
        fontSize: 13,
        whiteSpace: 'nowrap',
        minWidth: 36,
        textAlign: 'right',
        flexShrink: 0,
    },
}));

type TAudioRecorderBarProps = {
    status: TAudioRecorderStatus;
    durationMs: number;
    previewUrl: string | null;
    waveformLevels: number[];
    canResumeRecording: boolean;
    onDiscard: () => void;
    onPause: () => void;
    onResume: () => void;
};

export const AudioRecorderBar = ({
    status,
    durationMs,
    previewUrl,
    waveformLevels,
    canResumeRecording,
    onDiscard,
    onPause,
    onResume,
}: TAudioRecorderBarProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const primaryColor = theme.palette.primary.main;
    const isPaused = status === 'paused';

    return (
        <Box className={classes.root}>
            <IconButton
                className={classes.iconButton}
                onClick={onDiscard}
                aria-label="discard-audio"
            >
                <Trash2 size={18} />
            </IconButton>

            {isPaused ? (
                <>
                    <Box className={classes.center}>
                        {previewUrl ? (
                            <InlineAudioPlayer
                                key={previewUrl}
                                src={previewUrl}
                                activeColor={primaryColor}
                                inactiveColor={alpha(primaryColor, 0.45)}
                            />
                        ) : (
                            <>
                                <AudioWaveform
                                    levels={waveformLevels}
                                    progress={100}
                                    activeColor={primaryColor}
                                    inactiveColor={alpha(primaryColor, 0.45)}
                                    showScrubber={false}
                                />
                                <Typography
                                    className={classes.timer}
                                    variant="caption"
                                >
                                    {formatRecorderDuration(durationMs)}
                                </Typography>
                            </>
                        )}
                    </Box>

                    <IconButton
                        className={classes.iconButton}
                        onClick={onResume}
                        disabled={!canResumeRecording}
                        aria-label="resume-audio"
                    >
                        <Mic fontSize="small" />
                    </IconButton>
                </>
            ) : (
                <>
                    <IconButton
                        className={classes.iconButton}
                        onClick={onPause}
                        aria-label="pause-audio"
                    >
                        <Pause fontSize="small" />
                    </IconButton>

                    <Box className={classes.center}>
                        <AudioWaveform
                            levels={waveformLevels}
                            progress={100}
                            activeColor={primaryColor}
                            inactiveColor={alpha(primaryColor, 0.45)}
                            showScrubber={false}
                        />
                        <Typography className={classes.timer} variant="caption">
                            {formatRecorderDuration(durationMs)}
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    );
};
